import { useEffect, useId, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { Box, HStack, Icon, IconButton, Text, useDisclosure } from '@chakra-ui/react'
import { FiMessageCircle, FiX, FiZap } from 'react-icons/fi'
import { useTranslation } from 'react-i18next'
import { ChatPanel } from '@/features/chat/ChatPanel'
import { useAssistantChat } from '@/features/chat/agent/useAssistantChat'
import { useChatActionBridge } from '@/features/chat/agent/chatActionBridgeContext'
import { SideDrawer } from '@/components/shared'

/**
 * Global chat entry point available from every RootLayout page: a round FAB
 * (bottom-right, out of the Sidebar's way) that slides a ChatPanel drawer in from the
 * right edge. While the drawer is open the FAB steps aside and the drawer's own header
 * carries the close button, so nothing floats over the chat input. Mounts its own controller — closing and reopening the drawer does not
 * carry conversation state to/from ChatPage's separate instance.
 *
 * On /auto-test the same conversation switches to the agent chat that can operate the
 * vECU test generator (see useAssistantChat); the drawer then docks beside the page
 * instead of dimming it, so the wizard stays visible while the Chat drives it. The
 * header always states whether replies come from Mock or Live.
 */
export function FloatingChatDrawer() {
  const { t } = useTranslation()
  const location = useLocation()
  const { open, onClose, onOpen } = useDisclosure()
  const drawerId = useId()
  const [hasOpened, setHasOpened] = useState(false)
  const bridge = useChatActionBridge()
  const { controller, ctaSlot, renderBlocks, agentActive, busy, loadingLabel, emptyHint, suggestions, title } = useAssistantChat(location.pathname)

  const handleOpen = () => {
    if (!hasOpened) setHasOpened(true)
    onOpen()
  }

  // Launches from chat (/auto-test?workflowSessionId=…) open the drawer.
  const openSeq = bridge?.openRequest?.seq
  useEffect(() => {
    if (!openSeq) return
    setHasOpened(true)
    onOpen()
  }, [openSeq, onOpen])

  const setChatOpen = bridge?.setChatOpen
  useEffect(() => {
    setChatOpen?.(open)
  }, [open, setChatOpen])

  return (
    <>
      {/* Floating trigger */}
      <Box
        as="button"
        aria-label={t('chat.floating.open')}
        aria-expanded={open}
        aria-controls={drawerId}
        aria-hidden={open || undefined}
        tabIndex={open ? -1 : undefined}
        onClick={handleOpen}
        position="fixed"
        bottom="24px"
        right="24px"
        zIndex={1400}
        w="56px"
        h="56px"
        borderRadius="full"
        bg="brand.500"
        color="bg.surface"
        display="flex"
        alignItems="center"
        justifyContent="center"
        boxShadow="0 4px 16px rgba(0, 0, 0, 0.35)"
        cursor="pointer"
        opacity={open ? 0 : 1}
        transform={open ? 'scale(0.8)' : undefined}
        pointerEvents={open ? 'none' : undefined}
        transition="transform 0.15s ease, box-shadow 0.15s ease, opacity 0.15s ease"
        _hover={{ transform: 'scale(1.05)', boxShadow: '0 6px 20px rgba(0, 0, 0, 0.45)' }}
      >
        <Icon as={FiMessageCircle} boxSize={6} />
      </Box>

      <SideDrawer
        id={drawerId}
        open={open}
        onClose={onClose}
        aria-label={t('chat.floating.title')}
        bg="bg.app"
        keepMounted
        scrim={!agentActive}
      >
        {/* Defer mounting ChatPanel until first open, not on every page load. */}
        {hasOpened && (
          <>
            <HStack
              px={4}
              py={1.5}
              borderBottom="1px solid"
              borderColor="border"
              justify="space-between"
              gap={2}
              flexShrink={0}
            >
              <HStack gap={2} minW={0}>
                {agentActive ? (
                  <>
                    <Icon as={FiZap} color="brand.500" boxSize={3.5} flexShrink={0} />
                    {title && (
                      <Text fontSize="sm" fontWeight="medium" truncate>
                        {title}
                      </Text>
                    )}
                  </>
                ) : (
                  <Text fontSize="sm" fontWeight="medium" truncate>
                    {t('chat.floating.title')}
                  </Text>
                )}
              </HStack>
              <IconButton
                aria-label={t('chat.floating.close')}
                onClick={onClose}
                variant="ghost"
                size="sm"
                color="fg.muted"
                _hover={{ color: 'fg', bg: 'bg.surface-2' }}
              >
                <FiX />
              </IconButton>
            </HStack>
            <Box flex="1" minH={0} display="flex" flexDirection="column">
              <ChatPanel
                messages={controller.messages}
                inputValue={controller.inputValue}
                onInputChange={controller.setInputValue}
                onSend={controller.send}
                loading={busy}
                loadingLabel={loadingLabel}
                ctaSlot={ctaSlot}
                renderBlocks={renderBlocks}
                emptyHint={emptyHint}
                suggestions={suggestions}
                compact
              />
            </Box>
          </>
        )}
      </SideDrawer>
    </>
  )
}
